import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  deskAgingQuery,
  deskConsoleRunQuery,
  deskEmergencyStop,
  deskEscalationsQuery,
  deskFailuresQuery,
  deskHistoryQuery,
  deskQueueQuery,
  deskRunAction,
  deskWaitingQuery,
  type DeskAction,
  type DeskQueueFilters,
  type DeskReplayResult,
  type DeskSla,
} from '../api/desk'
import { errorMessages } from '../api/client'
import { InnerTabs } from '../ui/InnerTabs'
import { handedReasonLabel } from './handedReasons'
import './deskScreens.css'

export const HEARTBEAT_STALE_SECONDS = 120
const PER_PAGE = 50
const FAILURE_KINDS = ['lab_error', 'timeout', 'budget', 'not_configured', 'no_finish', 'cancelled'] as const
const FAILURE_COLORS: Record<string, string> = {
  lab_error: '#d9534f',
  timeout: '#f0ad4e',
  budget: '#5bc0de',
  not_configured: '#777',
  no_finish: '#9b59b6',
  cancelled: '#bbb',
}
const STATE_TONE: Record<string, string> = {
  queued: 'label-default',
  running: 'label-primary',
  executing: 'label-primary',
  waiting_user: 'label-info',
  waiting_confirm: 'label-info',
  waiting_plan: 'label-info',
  waiting_approval: 'label-info',
  waiting_order: 'label-info',
  waiting_external: 'label-info',
  verifying: 'label-info',
  handed_over: 'label-warning',
  resolved: 'label-success',
  closed: 'label-default',
}
const TABS = [
  { id: 'queue', label: 'Queue' },
  { id: 'waiting', label: 'Waiting on a person' },
  { id: 'escalations', label: 'Escalations' },
  { id: 'aging', label: 'Aging' },
  { id: 'failures', label: 'Failures' },
  { id: 'history', label: 'History' },
]

function invalidateDesk(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ['autonomy', 'desk'] })
}

function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`
}

function StateChip({ state }: { state: string }) {
  return <span className={`label ${STATE_TONE[state] ?? 'label-default'}`}>{state}</span>
}

function SlaCell({ sla }: { sla: DeskSla }) {
  if (!sla) return <span className="text-muted">-</span>
  return sla.breached ? (
    <span className="text-danger">breached by {duration(-sla.remaining)}</span>
  ) : (
    <span>{duration(sla.remaining)} left</span>
  )
}

function failText(error: unknown): string {
  return errorMessages(error).join(' ') || 'The desk did not answer. Try again in a moment.'
}

function Failed({ error }: { error: unknown }) {
  return <p className="text-danger">{failText(error)}</p>
}

function NoRows({ cols, pending, text }: { cols: number; pending: boolean; text: string }) {
  return (
    <tr>
      <td colSpan={cols} className="text-muted">
        {pending ? 'Loading...' : text}
      </td>
    </tr>
  )
}

function initialRun(): number | null {
  const id = Number(new URLSearchParams(window.location.search).get('run') ?? '')
  return Number.isInteger(id) && id > 0 ? id : null
}

export function DeskConsolePage() {
  const [tab, setTab] = useState('queue')
  const [runId, setRunId] = useState<number | null>(initialRun)

  return (
    <div className="container od-desk od-desk-console" style={{ paddingTop: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <h2 style={{ marginTop: 0 }}>Service desk console</h2>
        <EmergencyStop />
      </div>
      <InnerTabs tabs={TABS} current={tab} onPick={setTab} />
      <div style={{ paddingTop: 16 }}>
        {tab === 'queue' && <QueueTab onOpen={setRunId} />}
        {tab === 'waiting' && <WaitingTab />}
        {tab === 'escalations' && <EscalationsTab onOpen={setRunId} />}
        {tab === 'aging' && <AgingTab onOpen={setRunId} />}
        {tab === 'failures' && <FailuresTab onOpen={setRunId} />}
        {tab === 'history' && <HistoryTab />}
      </div>
      {runId !== null && <RunDrawer runId={runId} onClose={() => setRunId(null)} />}
    </div>
  )
}

function EmergencyStop() {
  const qc = useQueryClient()
  const [armed, setArmed] = useState(false)
  const [reason, setReason] = useState('')
  const stop = useMutation({
    mutationFn: () => deskEmergencyStop(reason.trim()),
    onSuccess: () => {
      setArmed(false)
      setReason('')
      void invalidateDesk(qc)
    },
  })
  if (!armed) {
    return (
      <div style={{ textAlign: 'right' }}>
        <button type="button" className="btn btn-danger od-desk-emergency" onClick={() => setArmed(true)}>
          Stop and retract
        </button>
        {stop.data && (
          <p className="text-success" style={{ marginTop: 6 }}>
            Desk paused: {stop.data.challenges} code(s) revoked, {stop.data.commands} command(s) cancelled, {stop.data.runs} run(s) stopped.
          </p>
        )}
      </div>
    )
  }
  return (
    <div className="panel panel-danger" style={{ width: 420, maxWidth: '100%', marginBottom: 0 }}>
      <div className="panel-body">
        <p>
          Pauses the AI desk everywhere, revokes every open code and approval link, cancels every desk command no machine has picked up, and stops the
          runs in progress. Write why.
        </p>
        <textarea
          className="form-control"
          aria-label="Why"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {stop.isError && <Failed error={stop.error} />}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-danger"
            disabled={reason.trim().length < 10 || stop.isPending}
            onClick={() => stop.mutate()}
          >
            Confirm stop and retract
          </button>
          <button type="button" className="btn btn-default" onClick={() => setArmed(false)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function QueueTab({ onOpen }: { onOpen(id: number): void }) {
  const [filters, setFilters] = useState<DeskQueueFilters>({ page: 1 })
  const [q, setQ] = useState('')
  const { data, isPending, isError, error } = useQuery({
    ...deskQueueQuery(filters),
    refetchInterval: (query) => (query.state.data?.rows.some((r) => r.state === 'running') ? 15000 : false),
  })
  const page = data?.page ?? 1
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PER_PAGE))

  return (
    <div>
      <form
        className="form-inline"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}
        onSubmit={(e) => {
          e.preventDefault()
          setFilters({ ...filters, q: q.trim(), page: 1 })
        }}
      >
        <select
          className="form-control"
          aria-label="State"
          value={filters.state ?? ''}
          onChange={(e) => setFilters({ ...filters, state: e.target.value, page: 1 })}
        >
          <option value="">Every state</option>
          {Object.keys(STATE_TONE).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          aria-label="Channel"
          value={filters.channel ?? ''}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value, page: 1 })}
        >
          <option value="">Every channel</option>
          <option value="email">email</option>
          <option value="portal">portal</option>
          <option value="ask">ask</option>
          <option value="finding">finding</option>
        </select>
        <input className="form-control" aria-label="Search" placeholder="Ticket # or subject" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className="btn btn-default">
          Search
        </button>
      </form>
      {isError && <Failed error={error} />}
      <div className="table-responsive">
        <table className="table table-condensed table-hover">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Company</th>
              <th>Subject</th>
              <th>State</th>
              <th>Type</th>
              <th>Path</th>
              <th>Priority</th>
              <th>Round</th>
              <th>Tier</th>
              <th>Worker</th>
              <th>Since</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((row) => {
              const stale = row.heartbeat_age_s !== null && row.heartbeat_age_s > HEARTBEAT_STALE_SECONDS
              return (
                <tr key={row.run_id} className={stale ? 'danger' : ''} data-run={row.run_id} style={{ cursor: 'pointer' }} onClick={() => onOpen(row.run_id)}>
                  <td>
                    <a href={`/app/easeedesk/ticket/${row.ticket_id}`} onClick={(e) => e.stopPropagation()}>
                      #{row.ticket_id}
                    </a>
                  </td>
                  <td>{row.company}</td>
                  <td>{row.subject}</td>
                  <td>
                    <StateChip state={row.state} />
                  </td>
                  <td>{row.request_type}</td>
                  <td>{row.path}</td>
                  <td>{row.priority}</td>
                  <td>{row.rounds}</td>
                  <td>{row.tier}</td>
                  <td>
                    {row.claimed_by}
                    {row.heartbeat_age_s !== null && <span className={stale ? 'text-danger' : 'text-muted'}> ({row.heartbeat_age_s}s)</span>}
                  </td>
                  <td>{row.since}</td>
                </tr>
              )
            })}
            {!isError && (data?.rows ?? []).length === 0 && <NoRows cols={11} pending={isPending} text="No runs match." />}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button type="button" className="btn btn-default btn-sm" disabled={page <= 1} onClick={() => setFilters({ ...filters, page: page - 1 })}>
          Previous
        </button>
        <span>
          Page {page} of {pages} ({data?.total ?? 0} runs)
        </span>
        <button type="button" className="btn btn-default btn-sm" disabled={page >= pages} onClick={() => setFilters({ ...filters, page: page + 1 })}>
          Next
        </button>
      </div>
    </div>
  )
}

function waitsForText(waitsFor: unknown): string {
  const w = (waitsFor ?? {}) as { kind?: string; name?: string }
  if (w.kind === 'requester') return w.name ? `${w.name} (requester)` : 'the requester'
  return w.name || 'assignee'
}

function WaitingTab() {
  const { data, isPending, isError, error } = useQuery({ ...deskWaitingQuery(), refetchInterval: 60000 })
  const counts = (data?.counts ?? {}) as Record<string, number>
  const rows = (data?.rows ?? []) as Array<Record<string, unknown>>

  return (
    <div>
      <h3>Waiting on a person</h3>
      {isError && <Failed error={error} />}
      <p>
        Plan: {counts.waiting_plan ?? 0} · Approval: {counts.waiting_approval ?? 0} · Order: {counts.waiting_order ?? 0} · User:{' '}
        {counts.waiting_user ?? 0} · Confirmation code: {counts.waiting_confirm ?? 0}
      </p>
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Subject</th>
              <th>State</th>
              <th>Waits for</th>
              <th>Age</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.run_id)}>
                <td>#{String(row.ticket_id)}</td>
                <td>{String(row.subject ?? '')}</td>
                <td>{String(row.state ?? '')}</td>
                <td className="od-waits-for">{waitsForText(row.waits_for)}</td>
                <td>{Math.floor(Number(row.age_seconds ?? 0) / 60)}m</td>
                <td>
                  <a href={`/app/${String(row.href ?? '')}`}>Open</a>
                </td>
              </tr>
            ))}
            {!isError && rows.length === 0 && <NoRows cols={6} pending={isPending} text="Nobody owes the desk an answer." />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EscalationsTab({ onOpen }: { onOpen(id: number): void }) {
  const qc = useQueryClient()
  const { data, isPending, isError, error } = useQuery(deskEscalationsQuery())
  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: DeskAction }) => deskRunAction(id, action),
    onSuccess: () => invalidateDesk(qc),
  })
  const rows = data?.rows ?? []

  return (
    <div>
      {isError && <Failed error={error} />}
      {act.isError && <Failed error={act.error} />}
      <div className="table-responsive">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Subject</th>
              <th>Assignee</th>
              <th>Handed over</th>
              <th>Why</th>
              <th>SLA</th>
              <th>Colleague</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.run_id}>
                <td>
                  <a href="#run" onClick={(e) => (e.preventDefault(), onOpen(row.run_id))}>
                    #{row.ticket_id}
                  </a>
                </td>
                <td>{row.subject}</td>
                <td>{row.assignee || <span className="text-muted">nobody</span>}</td>
                <td>{row.handed_at}</td>
                <td>{handedReasonLabel(row.handed_reason)}</td>
                <td>
                  <SlaCell sla={row.sla} />
                </td>
                <td>{row.colleague_replied_at ? `replied at ${row.colleague_replied_at}` : <span className="text-warning">nobody yet</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {row.renotifyable && (
                    <button type="button" className="btn btn-xs btn-default" onClick={() => act.mutate({ id: row.run_id, action: 'renotify' })}>
                      Re-notify
                    </button>
                  )}{' '}
                  {!row.acknowledged && (
                    <button type="button" className="btn btn-xs btn-default" onClick={() => act.mutate({ id: row.run_id, action: 'confirm-waiting' })}>
                      Confirm
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!isError && rows.length === 0 && <NoRows cols={8} pending={isPending} text="Nothing handed over to a person." />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AgingTab({ onOpen }: { onOpen(id: number): void }) {
  const { data, isPending, isError, error } = useQuery(deskAgingQuery())
  const rows = data?.rows ?? []

  return (
    <div>
      {isError && <Failed error={error} />}
      <div className="table-responsive">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Subject</th>
              <th>State</th>
              <th>Waits for</th>
              <th>Business hours</th>
              <th>Wall clock</th>
              <th>SLA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.run_id} className={row.sla?.breached ? 'danger' : ''}>
                <td>
                  <a href="#run" onClick={(e) => (e.preventDefault(), onOpen(row.run_id))}>
                    #{row.ticket_id}
                  </a>
                </td>
                <td>{row.subject}</td>
                <td>
                  <StateChip state={row.state} />
                </td>
                <td>{row.waits_for.kind}</td>
                <td>{duration(row.age_business_seconds)}</td>
                <td className="text-muted">{duration(row.age_seconds)}</td>
                <td>
                  <SlaCell sla={row.sla} />
                </td>
              </tr>
            ))}
            {!isError && rows.length === 0 && <NoRows cols={7} pending={isPending} text="Nothing is waiting." />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FailuresTab({ onOpen }: { onOpen(id: number): void }) {
  const { data, isPending, isError, error } = useQuery(deskFailuresQuery(30))
  const today = data?.today ?? []
  const days = data?.days ?? []
  const max = Math.max(1, ...days.map((d) => FAILURE_KINDS.reduce((n, k) => n + Number(d[k] ?? 0), 0)))

  return (
    <div>
      {isError && <Failed error={error} />}
      <p>
        {data?.total ?? 0} failed run(s) in 30 days.{' '}
        {FAILURE_KINDS.map((k) => (
          <span key={k} style={{ marginRight: 10 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, background: FAILURE_COLORS[k] }} /> {k}
          </span>
        ))}
      </p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, borderBottom: '1px solid #ddd' }} aria-label="Failures by day">
        {days.map((d) => (
          <div key={String(d.day)} title={String(d.day)} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse' }}>
            {FAILURE_KINDS.map((k) =>
              Number(d[k] ?? 0) > 0 ? (
                <div key={k} style={{ height: (Number(d[k]) / max) * 116, background: FAILURE_COLORS[k] }} title={`${String(d.day)} ${k}: ${String(d[k])}`} />
              ) : null,
            )}
          </div>
        ))}
      </div>
      <h4 style={{ marginTop: 20 }}>Failed today</h4>
      <div className="table-responsive">
        <table className="table table-condensed">
          <tbody>
            {today.map((r) => (
              <tr key={String(r.run_id)}>
                <td>
                  <a href="#run" onClick={(e) => (e.preventDefault(), onOpen(Number(r.run_id)))}>
                    #{String(r.ticket_id)}
                  </a>
                </td>
                <td>{String(r.subject ?? '')}</td>
                <td>{String(r.failure_kind ?? '')}</td>
                <td>{String(r.at ?? '')}</td>
              </tr>
            ))}
            {!isError && today.length === 0 && <NoRows cols={4} pending={isPending} text="No run failed today." />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function money(v: number | null): string {
  return v === null ? '-' : `$${v.toFixed(2)}`
}

function rate(confirmed: number, held: number, silent: number): string {
  const n = confirmed + held + silent
  return n > 0 ? `${Math.round((confirmed / n) * 100)}%` : '-'
}

function HistoryTab() {
  const { data, isPending, isError, error } = useQuery(deskHistoryQuery())
  const weeks = data?.weeks ?? []
  const byType = data?.by_type ?? []

  return (
    <div>
      {isError && <Failed error={error} />}
      <div className="table-responsive">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Week</th>
              <th>Taken</th>
              <th>Confirmed rate</th>
              <th>Reopened</th>
              <th>Handed over</th>
              <th>Cost per confirmed</th>
              <th>CSAT</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week}>
                <td>{w.week}</td>
                <td>{w.taken}</td>
                <td title="Confirmed over confirmed + held + silent; silence is not counted as a success">{rate(w.confirmed, w.held, w.silent)}</td>
                <td>{w.reopened}</td>
                <td>{w.handed}</td>
                <td>{money(w.cost_per_confirmed)}</td>
                <td>{w.csat ?? '-'}</td>
              </tr>
            ))}
            {!isError && weeks.length === 0 && <NoRows cols={7} pending={isPending} text="No finished week yet." />}
          </tbody>
        </table>
      </div>
      <h4>By request type</h4>
      <div className="table-responsive">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Type</th>
              <th>Taken</th>
              <th>Rate</th>
              <th>Cost per confirmed</th>
            </tr>
          </thead>
          <tbody>
            {byType.map((t) => (
              <tr key={t.request_type}>
                <td>{t.request_type}</td>
                <td>{t.taken}</td>
                <td>{t.rate === null ? '-' : `${Math.round(t.rate * 100)}%`}</td>
                <td>{money(t.cost_per_confirmed)}</td>
              </tr>
            ))}
            {!isError && byType.length === 0 && <NoRows cols={4} pending={isPending} text="No request type yet." />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Un buton care cere al doilea click; `needsReason` cere si motivul inainte. */
function ArmedButton({
  label,
  needsReason,
  busy,
  onFire,
}: {
  label: string
  needsReason?: boolean
  busy: boolean
  onFire(reason: string): void
}) {
  const [armed, setArmed] = useState(false)
  const [reason, setReason] = useState('')
  if (!armed) {
    return (
      <button type="button" className="btn btn-default btn-sm" onClick={() => setArmed(true)}>
        {label}
      </button>
    )
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {needsReason && (
        <input className="form-control input-sm" aria-label={`${label} reason`} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      )}
      <button
        type="button"
        className="btn btn-warning btn-sm"
        disabled={busy || (needsReason === true && reason.trim() === '')}
        onClick={() => {
          onFire(reason.trim())
          setArmed(false)
        }}
      >
        Confirm {label.toLowerCase()}
      </button>
      <button type="button" className="btn btn-link btn-sm" onClick={() => setArmed(false)}>
        Cancel
      </button>
    </span>
  )
}

function ReplayBox({ result }: { result: DeskReplayResult }) {
  const would = Array.isArray(result.would) ? result.would : []
  return (
    <div className="alert alert-info">
      <p style={{ marginBottom: 6 }}>
        Replay (dry run): {String(result.outcome ?? '')}. {String(result.reply ?? '')}
      </p>
      {would.length === 0 ? (
        <p className="text-muted" style={{ marginBottom: 0 }}>
          Would run nothing.
        </p>
      ) : (
        <>
          <strong>Would:</strong>
          <ul style={{ marginBottom: 0 }}>
            {would.map((w, i) => (
              <li key={i}>
                {`${w.tool}${w.target ? ` on ${w.target}` : ''}${w.args_keys?.length ? ` (${w.args_keys.join(', ')})` : ''}: ${w.decision}${w.reason ? ` - ${w.reason}` : ''}`}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function RunDrawer({ runId, onClose }: { runId: number; onClose(): void }) {
  const qc = useQueryClient()
  const { data, isPending, isError, error } = useQuery(deskConsoleRunQuery(runId))
  const [round, setRound] = useState(1)
  const act = useMutation({
    mutationFn: ({ action, body }: { action: DeskAction; body?: Record<string, unknown> }) => deskRunAction(runId, action, body),
    onSuccess: () => invalidateDesk(qc),
  })
  const run = data?.run
  const can = data?.can

  return (
    <aside
      className="od-desk-drawer"
      aria-label="Run"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 100vw)', background: '#fff', boxShadow: '-4px 0 16px rgba(0,0,0,.15)',
        overflowY: 'auto', padding: 20, zIndex: 1050,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <h3 style={{ marginTop: 0, flex: 1 }}>
          {run ? (
            <>
              <a href={`/app/easeedesk/ticket/${run.ticket_id}`}>Ticket #{run.ticket_id}</a> <StateChip state={run.state} />
            </>
          ) : (
            `Run #${runId}`
          )}
        </h3>
        <button type="button" className="close" aria-label="Close" onClick={onClose} style={{ float: 'none' }}>
          ×
        </button>
      </div>
      {isError && <Failed error={error} />}
      {isPending && <p className="text-muted">Loading...</p>}
      {run && can && (
        <>
          <p>
            {String(run.company_name ?? '')} · {String(run.requester ?? '')} · {String(run.request_type ?? '')} · path {String(run.path ?? '')} · tier{' '}
            {String(run.tier ?? '-')} · ${Number(run.cost_usd ?? 0).toFixed(4)}
            {run.outcome_label ? ` · label ${String(run.outcome_label)}` : ''}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {can.stop && <ArmedButton label="Stop" needsReason busy={act.isPending} onFire={(reason) => act.mutate({ action: 'stop', body: { reason } })} />}
            {can.handover && (
              <ArmedButton label="Hand over" needsReason busy={act.isPending} onFire={(reason) => act.mutate({ action: 'handover', body: { reason } })} />
            )}
            {can.giveback && <ArmedButton label="Give back to Adam" busy={act.isPending} onFire={() => act.mutate({ action: 'giveback' })} />}
            {can.renotify && <ArmedButton label="Re-notify" busy={act.isPending} onFire={() => act.mutate({ action: 'renotify' })} />}
            {can.confirm && (
              <button type="button" className="btn btn-default btn-sm" disabled={act.isPending} onClick={() => act.mutate({ action: 'confirm-waiting' })}>
                Confirm seen
              </button>
            )}
            {can.replay && (
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <select className="form-control input-sm" aria-label="Round" value={round} onChange={(e) => setRound(Number(e.target.value))}>
                  {Array.from({ length: Math.max(1, run.rounds) }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      round {n}
                    </option>
                  ))}
                </select>
                <ArmedButton label="Replay" busy={act.isPending} onFire={() => act.mutate({ action: 'replay', body: { round_no: round } })} />
              </span>
            )}
          </div>
          {act.isError && <Failed error={act.error} />}
          {act.data && 'would' in act.data && <ReplayBox result={act.data as unknown as DeskReplayResult} />}
          <h4>Steps</h4>
          <div className="table-responsive">
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Kind</th>
                  <th>Model</th>
                  <th>Outcome</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {data.steps.map((s, i) => (
                  <tr key={i}>
                    <td>{String(s.round_no)}</td>
                    <td>{String(s.kind ?? '')}</td>
                    <td>{String(s.model ?? '')}</td>
                    <td>
                      {String(s.outcome ?? '')} <span className="text-muted">{String(s.outcome_source ?? '')}</span>
                    </td>
                    <td>
                      {String(s.tokens_in ?? 0)}/{String(s.tokens_out ?? 0)}
                    </td>
                    <td>${Number(s.cost_usd ?? 0).toFixed(4)}</td>
                    <td>{String(s.started_at ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4>Open waits</h4>
          {data.waits.length === 0 ? <p className="text-muted">None.</p> : (
            <ul>
              {data.waits.map((w) => (
                <li key={String(w.id)}>
                  {String(w.kind)} #{String(w.ref_id)}, due {String(w.due_at)}
                </li>
              ))}
            </ul>
          )}
          <h4>Actions in the ledger</h4>
          {data.actions.length === 0 ? <p className="text-muted">None.</p> : (
            <ul>
              {data.actions.map((a) => (
                <li key={String(a.id)}>
                  {String(a.tool)} on {String(a.target_kind)} {String(a.target_id ?? '')}: {String(a.action_state)} ({String(a.decision)})
                </li>
              ))}
            </ul>
          )}
          <h4>Open codes and links</h4>
          {data.challenges.length === 0 ? <p className="text-muted">None.</p> : (
            <ul>
              {data.challenges.map((c) => (
                <li key={String(c.id)}>
                  {String(c.purpose)} by {String(c.channel)}, expires {String(c.expires_at)} UTC
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </aside>
  )
}
